<template>
  <div class="measureFilters"
    :class="{
      'relative': showMoreFilters
    }"
  >
    <div class="measureFilters-container">
      <div class="measureFilters-topRow">
        <div class="measureFilters-topRow-keywordWrapper">
          <neb-form-control label="Search"
            infoText="Search titles, urls, and keywords. Use quotation marks to search for an exact match."
          >
            <neb-input :value="keyword"
              id="keyword"
              name="keyword"
              placeholder="Search keywords, title, tags, or URLs"
              clearable
              button="search"
              @input="onKeywordInput"
              @clear="onKeywordInputClear"
              @action:submit="onKeywordInputSubmit"
            ></neb-input>
          </neb-form-control>
        </div>

        <div class="measureFilters-topRow-assetSourcesWrapper">
          <neb-form-control label="Source">
            <neb-toggle-box v-model="assetSources"
              :options="options.assetSourcesOptions"
              id="assetSources"
              name="assetSources"
            />
          </neb-form-control>
        </div>

        <div class="measureFilters-topRow-metricDateWrapper">
          <neb-form-control label="Metric Date Range"
            width="200px"
          >
            <neb-date-picker :value="metricDatePickerValue"
              mode="range"
              :defaultOptions="options.datePickerDefaultOptions"
              highlightDirty
              :defaultValue="[ '-10000', '0' ]"
              clearable
              :disableDayFunc="disableDayFunc"
              @input="onMetricDaysInput"
              @clear="onMetricDaysInput([ '-10000', '0' ])"
            ></neb-date-picker>
          </neb-form-control>
        </div>

        <div class="measureFilters-topRow-createdDateWrapper">
          <neb-form-control label="Date Created"
            width="200px"
          >
            <neb-date-picker :value="createdDatePickerValue"
              mode="range"
              :defaultOptions="options.datePickerDefaultOptions"
              highlightDirty
              :defaultValue="[ '-10000', '0' ]"
              clearable
              :disableDayFunc="disableDayFunc"
              @input="onCreatedDaysInput"
              @clear="onCreatedDaysInput([ '-10000', '0' ])"
            ></neb-date-picker>
          </neb-form-control>
        </div>
      </div>

      <div class="measureFilters-bottomRow">
        <div class="measureFilters-bottomRow-filterWrapper">
          <neb-form-control>
            <neb-multi-select id="measureFilters-brand"
              name="measureFilters-brand"
              placeholder="Brand"
              prefix="Brand"
              :options="brandOptions"
              v-model="brandIds"
              highlightDirty
              :defaultValue="[ `${brandId}` ]"
              clearable
              @clear="brandIds = [ `${brandId}` ]"
            ></neb-multi-select>
          </neb-form-control>
        </div>

        <div class="measureFilters-bottomRow-filterWrapper">
          <neb-form-control>
            <neb-select id="measureFilters-domain"
              name="measureFilters-domain"
              v-model="domain"
              :options="[ { label: 'All', value: '' } ].concat(domainOptions)"
              placeholder="Select domain"
              prefix="Domain"
              highlightDirty
              :defaultValue="''"
              clearable
              searchable
              @clear="domain = ''"
            ></neb-select>
          </neb-form-control>
        </div>

        <div class="measureFilters-bottomRow-filterWrapper">
          <neb-form-control>
            <neb-simple-range-select v-model="views"
              placeholder="All"
              prefix="Views"
              highlightDirty
              clearable
              :defaultValue="[]"
              :loading="filterMetricsLoading.pageViews"
              showMax
              showAvg
              :max="filterMetrics.pageViews.MAX__PAGE_VIEWS"
              :avg="filterMetrics.pageViews.AVG__PAGE_VIEWS"
              valueType="int"
              @action:openDropdown="onOpenRangeDropdown('pageViews')"
            />
          </neb-form-control>
        </div>

        <div class="measureFilters-bottomRow-filterWrapper">
          <neb-form-control>
            <neb-select id="measureFilters-distribution"
              name="measureFilters-distribution"
              prefix="Distribution"
              v-model="distribution"
              :options="distributionOptions"
              highlightDirty
              defaultValue="all"
              clearable
              @clear="distribution = 'all'"
            />
          </neb-form-control>
        </div>

        <div class="measureFilters-bottomRow-filterWrapper">
          <neb-form-control>
            <neb-simple-range-select v-model="completionRate"
              placeholder="All"
              prefix="Completion Rate"
              highlightDirty
              clearable
              :defaultValue="[]"
              :loading="filterMetricsLoading.completionRate"
              showMax
              showAvg
              :max="filterMetrics.completionRate.MAX__COMPLETION_RATE * 100"
              :avg="filterMetrics.completionRate.AVG__COMPLETION_RATE * 100"
              valueType="percent"
              @action:openDropdown="onOpenRangeDropdown('completionRate')"
            />
          </neb-form-control>
        </div>

        <div class="measureFilters-bottomRow-filterWrapper">
          <neb-form-control>
            <neb-simple-range-select v-model="conversionCount"
              placeholder="All"
              prefix="Conversions"
              highlightDirty
              clearable
              :defaultValue="[]"
              :loading="filterMetricsLoading.conversions"
              showMax
              showAvg
              :max="filterMetrics.conversions.MAX__CONVERSIONS"
              :avg="filterMetrics.conversions.AVG__CONVERSIONS"
              valueType="int"
              @action:openDropdown="onOpenRangeDropdown('conversions')"
            />
          </neb-form-control>
        </div>

        <div class="measureFilters-bottomRow-filterWrapper buttons">
          <neb-button type="ghost"
            @click="showMoreFilters = true"
          >
            More Filters
          </neb-button>
        </div>
      </div>

      <div class="measureFilters-extraFilters">
        <extra-filters :filters="filters"
          :propertyOptions="propertyOptions"
          :topicOptions="topicOptions"
          :languageOptions="languageOptions"
          @change="payload => $emit('change', payload)"
        ></extra-filters>
      </div>
    </div>

    <div class="measureFilters-moreFiltersContainer"
      v-show="showMoreFilters"
    >
      <div class="background"></div>

      <more-filters :filters="filters"
        :assetSourceOptions="options.assetSourcesOptions"
        :brandOptions="brandOptions"
        :datePickerOptions="options.datePickerDefaultOptions"
        :propertyOptions="propertyOptions"
        :topicOptions="topicOptions"
        :collectionOptions="collectionOptions"
        :languageOptions="languageOptions"
        :domainOptions="domainOptions"
        :visible="showMoreFilters"
        @cancel="showMoreFilters = false"
        @apply="onMoreFiltersApply"
      ></more-filters>
    </div>
  </div>
</template>

<script>
import { includes, isEmpty } from 'lodash'
import { datePickerDefaultOptions } from '@/options'

import NebToggleBox from '@/components/Assets/NebToggleBox'
import MoreFilters from './MoreFilters'
import ExtraFilters from './ExtraFilters'

const assetSourcesOptions = [ 'diffbot', 'nativo_advertiser' ]

export default {
  name: 'MeasureFilters',

  components: {
    NebToggleBox,
    MoreFilters,
    ExtraFilters
  },

  props: {
    filters: {
      type: Object,
      default: () => ({})
    },
    domainOptions: {
      type: Array,
      default: () => []
    },
    propertyOptions: {
      type: Array,
      default: () => []
    },
    topicOptions: {
      type: Array,
      default: () => []
    },
    collectionOptions: {
      type: Array,
      default: () => []
    },
    languageOptions: {
      type: Array,
      default: () => []
    }
  },

  data: () => ({
    options: {
      assetSourcesOptions,
      datePickerDefaultOptions
    },
    showMoreFilters: false,
    localKeyword: ''
  }),

  computed: {
    filterMetricsLoading () { return this.$store.state.measure.filterMetricsLoading },
    filterMetrics () { return this.$store.state.measure.filterMetrics },
    selectedFilterCount () { return this.$store.getters['measure/selectedFilterCount'] },
    keyword: {
      get () { return this.filters.keyword },
      set (value) { this.$emit('change', { key: 'keyword', value }) }
    },
    assetSources: {
      get () { return this.filters.assetSources },
      set (value) { this.$emit('change', { key: 'assetSources', value }) }
    },
    brandIds: {
      get () { return this.filters.brandIds },
      set (value) { this.$emit('change', { key: 'brandIds', value }) }
    },
    domain: {
      get () { return this.filters.domain },
      set (value) { this.$emit('change', { key: 'domain', value }) }
    },
    createdDateStart: {
      get () { return this.filters.createdDateStart },
      set (value) { this.$emit('change', { key: 'createdDateStart', value }) }
    },
    createdDateEnd: {
      get () { return this.filters.createdDateEnd },
      set (value) { this.$emit('change', { key: 'createdDateEnd', value }) }
    },
    metricDateStart: {
      get () { return this.filters.metricDateStart },
      set (value) { this.$emit('change', { key: 'metricDateStart', value }) }
    },
    metricDateEnd: {
      get () { return this.filters.metricDateEnd },
      set (value) { this.$emit('change', { key: 'metricDateEnd', value }) }
    },
    views: {
      get () { return this.filters.views },
      set (value) { this.$emit('change', {  key: 'views', value }) }
    },
    completionRate: {
      get () { return this.filters.completionRate },
      set (value) { this.$emit('change', { key: 'completionRate', value })}
    },
    conversionCount: {
      get () { return this.filters.conversionCount.value },
      set (value) { this.$emit('change', { key: 'conversionCount', value: { value } })}
    },
    brandOptions () {
      return this.$store.getters['brandsAsOptions']
    },
    distributionOptions () {
      return [
        { label: 'All', value: 'all' },
        { label: 'Sponsored', value: 'sponsored', disabled: !this.sponsoredContentEnabled },
        { label: 'Owned', value: 'owned' },
        { label: 'Earned', value: 'earned' }
      ]
    },
    distribution: {
      get () { return this.filters.distribution },
      set (value) { this.$emit('change', { key: 'distribution', value }) }
    },
    sponsoredContentEnabled () {
      if (this.brandIds.length === 1 && this.brandIds[0] === String(this.brandId)) {
        return true
      }

      const { competitors } = this.$store.state.brand.settings

      const allSelectedCompetitors = competitors
        .filter(obj => includes(this.brandIds, obj.id))

      return allSelectedCompetitors.every(obj => obj.enableSponsoredContent)
    },
    createdDatePickerValue () {
      return [ this.createdDateStart, this.createdDateEnd ]
    },
    metricDatePickerValue () {
      return [ this.metricDateStart, this.metricDateEnd ]
    }
  },

  methods: {
    async onOpenRangeDropdown (metric) {
      if (isEmpty(this.filterMetrics[metric])) {
        await this.$store.dispatch('measure/loadFilterMetrics', { metric, brandId: this.brandId })
      }
    },
    disableDayFunc (year, month, day) {
      const d1 = new Date(year, month, day)

      return d1 > new Date()
    },
    onMoreFiltersApply (payload) {
      this.showMoreFilters = false
      this.$emit('bulkChange', payload)
    },
    onKeywordInput (value) { this.localKeyword = value },
    onKeywordInputSubmit () { this.keyword = this.localKeyword },
    onKeywordInputClear () {
      this.localKeyword = ''
      this.keyword = ''
    },
    onCreatedDaysInput (value) {
      this.$emit('bulkChange', {
        createdDateStart: value[0],
        createdDateEnd: value[1]
      })
    },
    onMetricDaysInput (value) {
      this.$emit('bulkChange', {
        metricDateStart: value[0],
        metricDateEnd: value[1]
      })
    }
  }
}
</script>

<style lang="scss">
#measureFilters-brand {
  min-width: 180px;
}
.measureFilters {
  box-sizing: border-box;
  display: flex;
  justify-content: center;

  &.relative {
    position: relative;
  }

  &-moreFiltersContainer {
    position: absolute;
    top: 0;
    left: 0;

    .background {
      width: 100vw;
      height: 100vh;
      background-color: rgba(0,0,0,0.5);
      position: fixed;
      top: 0;
      left: 0;
      z-index: 6;
    }
  }

  &-container {
    box-sizing: border-box;
    width: 1185px;
  }

  &-topRow {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    padding-top: 1.5em;

    &-keywordWrapper {
      box-sizing: border-box;
      overflow: hidden;
      flex-basis: 33%;
      padding-right: 20px;
    }

    &-metricDateWrapper {
      margin-left: auto;
    }

    &-createdDateWrapper {
      margin-left: 10px;
    }
  }

  &-bottomRow {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: flex-start;

    &-filterWrapper {
      box-sizing: border-box;
      flex-grow: 1;
      max-width: 25%;

      &:not(:last-child) {
        padding-right: 10px;
      }

      &.buttons {
        display: flex;
        align-items: center;

        .el-button {
          height: 34px;
        }
      }
    }
  }
}
</style>
